// import hooks
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// import components
import { Box } from '@mui/material';
import { CustomDataGridActionMenuItem } from '../components/CustomDataGridActionMenuItem';
import { CustomDataGridAction } from '../components/customDataGridAction';
import { MoreActionsMenu } from '../../MoreActionsMenu';
import { CustomDataGridProps, DataGridAction } from '../DataGrid.types';

// import types
import { GridRenderCellParams } from '@mui/x-data-grid-pro';
import { GridColDef } from '@mui/x-data-grid/models/colDef/gridColDef';

const lengthOfTheButtons = 45;

const createAction = (
  onClick: DataGridAction['onClick'],
  icon: DataGridAction['icon'],
  tooltipTitle: DataGridAction['tooltipTitle'],
  disabled?: DataGridAction['disabled']
): DataGridAction => ({
  onClick,
  icon,
  tooltipTitle,
  disabled
});

export const useGetCustomDataGridColumnsToRender = (
  onViewClick: CustomDataGridProps['onViewClick'],
  onCopyClick: CustomDataGridProps['onCopyClick'],
  onEditClick: CustomDataGridProps['onEditClick'],
  onDeleteClick: CustomDataGridProps['onDeleteClick'],
  handleDeleteClick: (id: string | number) => void,
  customActions: CustomDataGridProps['customActions'],
  columns: CustomDataGridProps['columns'],
  actionsThreshold: number,
  disableActions: CustomDataGridProps['disableActions'],
  canBeEditedProp: string | undefined,
  canBeDeletedProp: string | undefined
) => {
  const { t } = useTranslation('common');

  const [shouldRenderActionColumn, setShouldRenderActionColumn] =
    useState(false);
  useEffect(() => {
    // if the actions aren't universally disabled, *and* at least 1 action
    // is specified, the action column should render
    if (
      !disableActions &&
      (onViewClick ||
        onCopyClick ||
        onEditClick ||
        onDeleteClick ||
        typeof customActions === 'function' || // can't be null if it is a function
        (!!customActions && customActions.length > 0)) // if customActions is not a function, it's an array or null
    ) {
      setShouldRenderActionColumn(true);
      return;
    }

    // if there is nothing to render or actions are not allowed, we don't
    // render the column at all
    setShouldRenderActionColumn(false);
  }, [
    onViewClick,
    onCopyClick,
    onEditClick,
    onDeleteClick,
    handleDeleteClick,
    customActions,
    disableActions
  ]);

  const renderActionButtonsForRow = useCallback(
    (params: GridRenderCellParams) => {
      if (!shouldRenderActionColumn) return;

      const actions: DataGridAction[] = [];
      const canBeEdited = !canBeEditedProp || params.row[canBeEditedProp];
      const canBeDeleted = !canBeDeletedProp || params.row[canBeDeletedProp];

      if (onViewClick) {
        actions.push(createAction(onViewClick, 'eye', t('details')));
      }
      if (onCopyClick) {
        actions.push(createAction(onCopyClick, 'copy', t('copy')));
      }
      if (onEditClick && canBeEdited) {
        actions.push(createAction(onEditClick, 'pen', t('edit'), true));
      }
      if (onDeleteClick && canBeDeleted) {
        actions.push(
          createAction(handleDeleteClick, 'trash-can', t('remove'), true)
        );
      }
      if (typeof customActions === 'function') {
        actions.push(...(customActions(params.id, params.row) ?? []));
      } else {
        actions.push(...(customActions ?? []));
      }

      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            height: '100%'
          }}
        >
          {actions.length <= actionsThreshold ? (
            actions.map((action, index) => (
              <CustomDataGridAction
                key={index}
                action={action}
                params={params}
              />
            ))
          ) : (
            <>
              {actions.slice(0, actionsThreshold - 1).map((action, index) => (
                <CustomDataGridAction
                  key={index}
                  action={action}
                  params={params}
                />
              ))}
              <MoreActionsMenu
                renderActions={(handleCloseMenu) =>
                  actions
                    .slice(actionsThreshold - 1)
                    .map((action, index) => (
                      <CustomDataGridActionMenuItem
                        key={index}
                        action={action}
                        params={params}
                        onCloseMenu={handleCloseMenu}
                      />
                    ))
                }
              />
            </>
          )}
        </Box>
      );
    },
    [
      onViewClick,
      onCopyClick,
      onEditClick,
      onDeleteClick,
      handleDeleteClick,
      customActions,
      shouldRenderActionColumn
    ]
  );

  return useMemo(
    () => [
      ...columns,
      ...(!shouldRenderActionColumn
        ? []
        : [
            {
              field: 'actions',
              headerName: t('actions'),
              width: lengthOfTheButtons * actionsThreshold,
              renderCell: renderActionButtonsForRow,
              minWidth: 75
            } as GridColDef
          ])
    ],
    [actionsThreshold, columns, renderActionButtonsForRow]
  );
};
